/**
 * Copyright 2020 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import * as React from 'react';
import { pageTypes } from '@/constants/page';
import { CertonaBlock } from '@/features/certona';
import type { CertonaBlockProps, CertonaRecommendation } from '@/features/certona';
import { getCartridgeType } from '@/utils/getCartridgeType';
import ContainOverflow from '@/components/AZCustomComponent/ContainOverflow';
import { Filter } from '@/components/Filter';
import { Grid } from '@/components/Grid';
import { useSearchPartTypes } from '../../api/getSearchPartTypes';
import { useSearchProductData } from '../../api/getSearchProductData';
import { SearchResults } from '../../components/SearchComponent/SearchResults';
import { YMMEContentBlock } from '@/features/ymme/components/YMMEContentBlock';
import styles from '@/components/PageTemplates/Generic/styles.module.scss';
import { ResultsList } from '@/features/shelf/components/PageTemplates/Shelf/ResultsList';
import { ListOptions } from '@/features/productListingPage/components/ListOptions/ListOptions';
import type { FilterNavContentBlock, ResultsListContentBlock, ShelfPart } from '@/types/legacy';
import { useLocale } from '@/hooks/useLocale';
import type { FilterDataType, HandleFilterType } from '@/types/filterTypes';
import { NumberOfResults } from '@/features/productListingPage/components/NumberOfResults';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { countryCodes } from '@/constants/locale';
import { useMonetateDecisionFlag } from '@/features/kibo/api/getKiboDecision';
import { persistXFusionQueryId } from '@/utils/xFusionQueryIdHelpers';
import AltYMMEContentBlock from '@/features/ymme/components/AltYMMEContentBlock';
import { useRef } from 'react';
import { useProductList } from '@/features/shelf/context/useProductList';
import { isContentBlockSlot } from '@/utils/isContentBlockSlot';
import type {
  LegacySearchContentBlock,
  HardcodedSearchContentBlock,
  UnspecifiedLegacySearchContentBlock,
} from '../../interface';
import { useHeaderData } from '@/features/header/api/getHeader';
import { CustomFallbackList } from './CustomFallbackList';

export type SearchPageTemplateProps = {
  content: HardcodedSearchContentBlock[];
  queryDisplayLabel?: string;
  certonaRecommendations?: CertonaRecommendation[];
  certonaOptions?: Omit<CertonaBlockProps, 'certonaRecommendations' | 'contentItem'>;
  disableRecordsPerPage?: boolean;
  rppOptionsData?: string;
  onFilterSelect?: (filter: HandleFilterType) => void;
  handleDealClick?: (
    part: ShelfPart,
    price: string,
    quantity: number,
    ref: React.RefObject<HTMLButtonElement>,
    fulfillmentTypeId: number | null
  ) => void;
  viewingPartTypes: boolean;
};

export const SearchPageTemplate = ({
  content,
  queryDisplayLabel,
  certonaRecommendations,
  certonaOptions,
  disableRecordsPerPage,
  rppOptionsData,
  handleDealClick,
  onFilterSelect,
  viewingPartTypes = false,
}: SearchPageTemplateProps) => {
  const locale = useLocale();
  const { data: shelfData } = useProductList();
  const { data: searchProductData } = useSearchProductData({
    enabled: !viewingPartTypes,
  });
  const { data: searchPartTypesData } = useSearchPartTypes({
    enabled: viewingPartTypes,
    viewingPartTypes,
  });
  const isMobile = useMediaQuery((theme) => theme.breakpoints.only('sm'));
  const isUS = locale === countryCodes.us;
  const useNewShelfDesignMobile = useMonetateDecisionFlag('useNewShelfDesignMobile') && isUS;
  const altYMMEContentBlock = useMonetateDecisionFlag('alternativeYMMEContentBlock');
  const resultsListRef = useRef<HTMLDivElement>(null);
  const { data: headerData } = useHeaderData();

  persistXFusionQueryId(searchProductData?.xFusionQueryId);

  const renderContentBlocks = (contentItem: UnspecifiedLegacySearchContentBlock, i: number) => {
    if (!isSpecificContentBlock(contentItem)) {
      return null;
    }

    const recsPerPage =
      (contentItem as ResultsListContentBlock).recsPerPage ??
      searchPartTypesData?.recordsPerPage ??
      searchProductData?.recordsPerPage;
    const totalNumRecs = shelfData?.shelfParts?.length ?? 0;
    const firstRecNum =
      (contentItem as ResultsListContentBlock).firstRecNum ??
      searchPartTypesData?.firstRecNum ??
      searchProductData?.firstRecNum;
    const staticFilterData: FilterDataType[] | undefined | null = viewingPartTypes
      ? searchPartTypesData?.staticNavigation ??
        (contentItem as FilterNavContentBlock).staticNavigation
      : searchProductData?.staticNavigation ??
        (contentItem as FilterNavContentBlock).staticNavigation;

    const key = `${contentItem.contentType}-${i}`;

    switch (contentItem.contentType) {
      case 'CustomFallback':
        return (
          <div className="htmlContentBlock" key={key}>
            <ContainOverflow key={key}>
              <CustomFallbackList key={key} locale={locale} />
            </ContainOverflow>
          </div>
        );

      case 'AZCombinedSearchResults':
        if (!viewingPartTypes) {
          return (
            <Grid item spacing={0} key={`ra-${key}`}>
              <ListOptions
                onFilterSelect={onFilterSelect}
                key={`${key}list`}
                pageType={pageTypes.search}
                disableRecordsPerPage={disableRecordsPerPage}
                rppOptionsData={rppOptionsData}
                staticFilterData={searchProductData?.staticNavigation}
                searchText={searchProductData?.correctedTerm ?? searchProductData?.searchedKeyword}
                resultsListRef={resultsListRef}
                viewingPartTypes={viewingPartTypes}
              />
              {useNewShelfDesignMobile && isMobile && (
                <Grid container spacing={0} className={styles.numberOfResultsContainer}>
                  <NumberOfResults
                    firstPageRec={searchProductData?.firstRecNum ?? 1}
                    recsPerPage={searchProductData?.recordsPerPage ?? 12}
                    totalNumRecs={searchProductData?.total ?? 0}
                    searchText={
                      searchProductData?.correctedTerm ?? searchProductData?.searchedKeyword
                    }
                  />
                </Grid>
              )}
              <ResultsList
                locale={locale}
                key={key}
                recsPerPage={searchProductData?.recordsPerPage ?? 0}
                totalNumRecs={searchProductData?.total ?? 0}
                firstRecNum={searchProductData?.firstRecNum ?? 0}
                isPaginationEnabled={
                  (searchProductData?.total ?? 0) > (searchProductData?.recordsPerPage ?? 12)
                }
                handleDealClick={handleDealClick}
                xFusionQueryId={searchProductData?.xFusionQueryId}
                resultsListRef={resultsListRef}
              />
            </Grid>
          );
        }
        return (
          <Grid container spacing={0} key={key}>
            <SearchResults
              searchResult={undefined}
              queryDisplayLabel={queryDisplayLabel}
              viewingPartTypes={viewingPartTypes}
            />
          </Grid>
        );

      case 'FilterNav':
        return (
          <Filter
            viewingPartTypes={viewingPartTypes}
            pageType={contentItem['@type']?.split('_')[0].toLowerCase()}
            staticFilterData={staticFilterData}
            dynamicFilterData={contentItem.dynamicNavigation?.attributeNarrowingList}
            onFilterSelect={onFilterSelect}
            key={key}
          />
        );

      case 'ResultsList':
        return (
          <Grid container spacing={0} key={key} className={styles.results}>
            <ResultsList
              locale={locale}
              key={key}
              recsPerPage={recsPerPage}
              totalNumRecs={totalNumRecs}
              firstRecNum={firstRecNum}
              isPaginationEnabled={totalNumRecs > recsPerPage}
              handleDealClick={handleDealClick}
              xFusionQueryId={searchProductData?.xFusionQueryId}
            />
          </Grid>
        );

      case 'YMME':
        return (
          <>
            {(!isMobile || (isMobile && !headerData?.vehicleMap.vehicleName)) && (
              <>
                {altYMMEContentBlock ? (
                  <AltYMMEContentBlock pageType={pageTypes.search} />
                ) : (
                  <ContainOverflow key={key} fullWidth={true}>
                    <YMMEContentBlock pageType={pageTypes.search} data={contentItem} />
                  </ContainOverflow>
                )}
              </>
            )}
          </>
        );

      case 'Certona':
        return (
          <CertonaBlock
            contentItem={contentItem}
            key={key}
            certonaRecommendations={certonaRecommendations}
            {...certonaOptions}
          />
        );

      default:
        return null;
    }
  };

  const contentExists = content != null;
  return (
    <>
      {contentExists &&
        content.map((contentItem, i) => {
          const contentType = getCartridgeType(contentItem['@type']);

          if (contentType === 'Slot' && isContentBlockSlot(contentItem)) {
            return contentItem.contents.map((singleContentItem, mappedContentIndex) =>
              renderContentBlocks(
                {
                  ...singleContentItem,
                  contentType: getCartridgeType(singleContentItem['@type']),
                },
                mappedContentIndex
              )
            );
          }

          return renderContentBlocks({ ...contentItem, contentType }, i);
        })}
    </>
  );
};

const contentTypes = new Set([
  'CustomFallback',
  'AZCombinedSearchResults',
  'FilterNav',
  'ResultsList',
  'YMME',
  'Certona',
]);

const isSpecificContentBlock = (
  contentItem: UnspecifiedLegacySearchContentBlock
): contentItem is LegacySearchContentBlock => {
  return contentTypes.has(contentItem.contentType);
};
